import React from 'react';
import Loading from '../teal-react/components/molecules/Loading';
import SimpleGrid from '../teal-react/components/organisms/SimpleGrid';
import SimpleTable from '../teal-react/components/organisms/SimpleTable';
import { AddOutlined, Edit } from '@mui/icons-material';
import { Alert, Box, Button } from '@mui/material';
// import SimpleModal from '../teal-react/components/organisms/SimpleModal';
import { useFormulaList } from '../graphql/queries';
import FormulaForm from './FormulaForm';
import Utils from '../teal-react/utils';
import K from '../Constants';
import { useAuth } from '../teal-react/utils/token';
import ExcludeReadOnly from './Auth/ExcludeReadOnly';

const getLbs = (amount: number, density: number, unit?: string) => {
  if (!unit) {
    return 0;
  }
  switch (unit) {
    case K.unit.Bags:
      return amount * 10;
    case K.unit.Cups:
      return (amount * density) / 8;
    case K.unit.Drums:
      return amount * density * 55;
    case K.unit.Gal:
      return amount * density;
    case K.unit.Grams:
      return amount / 454;
    case K.unit.Ounces:
      return (amount * density) / 128;
    case K.unit.Spoons:
      return (amount * 5) / 454;
    case K.unit.Totes:
      return amount * density * 275;

    default: // lbs
      return amount;
  }
};

const aggregateValues = (materials: any[]) => {
  //   The amount calculations by unit:
  //   - gallons: amount x density
  //   - bags: amount x 10
  //   - totes: amount x density x 275
  //   - drums: amount x density x 55
  //   - cups: amount x density x 1/8
  //   - ounces: amount x density x 1/128
  //   - grams: amount / 454
  //   - spoons: amount * 5/454
  //   - no unit: amount = 0
  let totalPrice = 0;
  let totalAmount = 0;
  let _amount = 0;
  for (const mat of materials) {
    _amount = getLbs(mat.amount, mat.material.density, mat.unit?.name);
    totalPrice += _amount * mat.material.price;
    totalAmount += _amount;
  }
  return { totalPrice, totalAmount };
};

const FormulasList = ({ product_id }: { product_id: number }) => {
  const { user } = useAuth();
  const [formula, setFormula] = React.useState<any>();
  const { data } = useFormulaList(product_id);

  React.useEffect(() => {
    setFormula(undefined);
  }, [product_id]);

  if (!data) {
    return <Loading />;
  }

  const items = data.formula.map((f: any) => ({
    size: 12,
    content: (
      <SimpleTable
        row_limit={100}
        header={`Formula: ${f.name}`}
        subheader={`${f.amount || ''} ${f.unit?.name || ''}`}
        data={f.materials}
        headeraction={
          user?.isRO
            ? undefined
            : {
                icon: <Edit />,
                onClick: () => {
                  setFormula(f);
                },
              }
        }
        columns={[
          {
            header: 'Material',
            cell: (r: any) => r.material.name,
          },
          {
            header: 'Amount',
            cell: (r: any) => `${r.amount || ''} ${r.unit?.name || ''}`,
            align: 'right',
          },
          {
            header: 'Price',
            cell: (r: any) => Utils.formatMoney(r.material.price),
            align: 'right',
          },
          {
            header: 'Cost',
            cell: (r: any) =>
              Utils.formatMoney(
                getLbs(r.amount, r.material.density, r.unit?.name) *
                  r.material.price
              ),
            align: 'right',
          },
          {
            header: 'Density',
            cell: (r: any) => Utils.formatNumber(r.material.density, 4),
            align: 'right',
          },
          {
            header: 'Weight %',
            cell: (r: any) => r.weight_percentage + '%',
            align: 'right',
          },
          {
            header: 'Remark',
          },
        ]}
        summaryRow={[
          {
            cell: () => 'Totals:',
            colSpan: 2,
          },
          {
            cell: (data: any) => {
              const { totalAmount, totalPrice } = aggregateValues(data);
              return `${Utils.formatMoney(totalPrice / totalAmount)} / lb`;
            },
            align: 'right',
          },
          {
            cell: (data: any) => {
              const { totalPrice } = aggregateValues(data);
              return Utils.formatMoney(totalPrice);
            },
            align: 'right',
          },
          {
            colSpan: 2,
            align: 'right',
            cell: (rows: any) => {
              const total = rows.reduce((a: number, i: any) => {
                return a + i.weight_percentage;
              }, 0);
              const rounded_total = +total.toFixed(3);
              if (rounded_total !== 100 && rounded_total !== 0) {
                return (
                  <Alert severity="warning" color="warning">
                    {rounded_total < 100
                      ? `Weight % Missing ${(100 - rounded_total).toFixed(3)}%`
                      : `Weight % off by ${(rounded_total - 100).toFixed(3)}%`}
                  </Alert>
                );
              }
              return null;
            },
          },
        ]}
      />
    ),
  }));
  return (
    <div>
      {formula ? (
        <FormulaForm formula={formula} onClose={() => setFormula(undefined)} />
      ) : (
        <>
          <SimpleGrid items={items} />
          {items.length === 0 ? (
            <Box sx={{ p: 4 }}>No formulas found.</Box>
          ) : null}
          <ExcludeReadOnly>
            <Box sx={{ p: 2 }}>
              <Button
                onClick={() => {
                  setFormula({
                    product_id,
                  });
                }}
                startIcon={<AddOutlined />}
              >
                Create New Formula
              </Button>
            </Box>
          </ExcludeReadOnly>
        </>
      )}
    </div>
  );
};

export default FormulasList;
